import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Phone, ArrowRight, CheckCircle, Clock, Shield, MapPin, CreditCard,
  Zap, Award, ThumbsUp, Home, Building, Store, Wrench, Key
} from 'lucide-react';
import { companyInfo } from '../../data/mock';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { FAQSection } from '../seo/FAQSection';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';

// Données uniques pour chaque ville du 94
const citiesData = {
  'noisy-le-grand': {
    name: 'Noisy-le-Grand',
    postalCode: '93160',
    department: 'Seine-Saint-Denis',
    lat: 48.8489,
    lng: 2.5522,
    population: '67 000',
    specificities: 'Avec ses grands ensembles résidentiels comme les Arènes de Picasso et le Palacio d\'Abraxas, Noisy-le-Grand présente des besoins spécifiques en rénovation de fenêtres pour améliorer l\'isolation thermique et phonique.',
    landmarks: ['Arènes de Picasso', 'Palacio d\'Abraxas', 'Mont d\'Est'],
    nearbyAreas: ['Villiers-sur-Marne', 'Bry-sur-Marne', 'Gournay-sur-Marne', 'Champs-sur-Marne']
  },
  'bry-sur-marne': {
    name: 'Bry-sur-Marne',
    postalCode: '94360',
    department: 'Val-de-Marne',
    lat: 48.8384,
    lng: 2.5225,
    population: '17 000',
    specificities: 'Ville résidentielle prisée du Val-de-Marne, Bry-sur-Marne dispose d\'un patrimoine architectural varié nécessitant des solutions de menuiserie adaptées, du pavillon ancien aux résidences modernes.',
    landmarks: ['Église Saint-Gervais-Saint-Protais', 'Parc du Tremblay', 'Bords de Marne'],
    nearbyAreas: ['Villiers-sur-Marne', 'Noisy-le-Grand', 'Le Perreux-sur-Marne', 'Nogent-sur-Marne']
  },
  'le-perreux-sur-marne': {
    name: 'Le Perreux-sur-Marne',
    postalCode: '94170',
    department: 'Val-de-Marne',
    lat: 48.8428,
    lng: 2.5039,
    population: '34 000',
    specificities: 'Le Perreux-sur-Marne, avec ses nombreuses maisons de caractère des années 1920-1930, nécessite une expertise particulière pour la rénovation de fenêtres dans le respect du style architectural.',
    landmarks: ['Bords de Marne', 'Centre-ville historique', 'Parc des Poupons'],
    nearbyAreas: ['Bry-sur-Marne', 'Nogent-sur-Marne', 'Neuilly-Plaisance', 'Neuilly-sur-Marne']
  },
  'chennevieres-sur-marne': {
    name: 'Chennevières-sur-Marne',
    postalCode: '94430',
    department: 'Val-de-Marne',
    lat: 48.7983,
    lng: 2.5350,
    population: '18 500',
    specificities: 'Située sur les coteaux de la Marne, Chennevières-sur-Marne présente un habitat diversifié, des maisons sur les hauteurs aux appartements en centre-ville, tous pouvant bénéficier d\'une rénovation énergétique.',
    landmarks: ['Coteaux de la Marne', 'Église Saint-Pierre', 'Fort de Champigny'],
    nearbyAreas: ['Champigny-sur-Marne', 'Le Plessis-Trévise', 'Ormesson-sur-Marne', 'Villiers-sur-Marne']
  },
  'le-plessis-trevise': {
    name: 'Le Plessis-Trévise',
    postalCode: '94420',
    department: 'Val-de-Marne',
    lat: 48.8119,
    lng: 2.5736,
    population: '20 000',
    specificities: 'Commune résidentielle réputée pour ses espaces verts, Le Plessis-Trévise compte de nombreux pavillons des années 70-80 qui peuvent significativement améliorer leur performance énergétique par le remplacement des menuiseries.',
    landmarks: ['Parc du Plessis', 'Église Notre-Dame', 'Centre commercial régional'],
    nearbyAreas: ['Villiers-sur-Marne', 'Chennevières-sur-Marne', 'La Queue-en-Brie', 'Champigny-sur-Marne']
  },
  'saint-maur-des-fosses': {
    name: 'Saint-Maur-des-Fossés',
    postalCode: '94100',
    department: 'Val-de-Marne',
    lat: 48.7997,
    lng: 2.4897,
    population: '76 000',
    specificities: 'Plus grande ville du Val-de-Marne, Saint-Maur-des-Fossés possède un patrimoine immobilier exceptionnel avec ses quartiers résidentiels huppés. La rénovation de fenêtres y requiert une attention particulière au standing.',
    landmarks: ['Boucle de la Marne', 'Parc de Saint-Maur', 'Abbaye Saint-Maur'],
    nearbyAreas: ['Créteil', 'Champigny-sur-Marne', 'Nogent-sur-Marne', 'Le Perreux-sur-Marne']
  },
  'neuilly-sur-marne': {
    name: 'Neuilly-sur-Marne',
    postalCode: '93330',
    department: 'Seine-Saint-Denis',
    lat: 48.8544,
    lng: 2.5478,
    population: '36 000',
    specificities: 'En pleine transformation urbaine avec le Grand Paris Express, Neuilly-sur-Marne voit ses besoins en rénovation augmenter, notamment pour améliorer l\'isolation acoustique face aux nouveaux axes de transport.',
    landmarks: ['Hôpital Ville-Évrard', 'Parc de la Mairie', 'Bords de Marne'],
    nearbyAreas: ['Noisy-le-Grand', 'Neuilly-Plaisance', 'Gournay-sur-Marne', 'Chelles']
  },
  'neuilly-plaisance': {
    name: 'Neuilly-Plaisance',
    postalCode: '93360',
    department: 'Seine-Saint-Denis',
    lat: 48.8614,
    lng: 2.5067,
    population: '21 000',
    specificities: 'Ville pavillonnaire par excellence, Neuilly-Plaisance présente un habitat majoritairement composé de maisons individuelles dont beaucoup peuvent bénéficier d\'une rénovation thermique des fenêtres.',
    landmarks: ['Église Saint-Jacques', 'Parc des Coteaux', 'Centre-ville commerçant'],
    nearbyAreas: ['Neuilly-sur-Marne', 'Le Perreux-sur-Marne', 'Rosny-sous-Bois', 'Noisy-le-Grand']
  },
  'gournay-sur-marne': {
    name: 'Gournay-sur-Marne',
    postalCode: '93460',
    department: 'Seine-Saint-Denis',
    lat: 48.8608,
    lng: 2.5772,
    population: '7 000',
    specificities: 'Plus petite commune de Seine-Saint-Denis, Gournay-sur-Marne conserve un caractère villageois avec un habitat principalement constitué de maisons anciennes nécessitant souvent une mise aux normes thermiques.',
    landmarks: ['Église Notre-Dame', 'Bords de Marne', 'Île de Gournay'],
    nearbyAreas: ['Noisy-le-Grand', 'Champs-sur-Marne', 'Neuilly-sur-Marne', 'Noisiel']
  },
  'torcy': {
    name: 'Torcy',
    postalCode: '77200',
    department: 'Seine-et-Marne',
    lat: 48.8508,
    lng: 2.6533,
    population: '24 000',
    specificities: 'Porte d\'entrée de la ville nouvelle de Marne-la-Vallée, Torcy combine immeubles collectifs récents et quartiers pavillonnaires. La rénovation énergétique y est un enjeu majeur pour les copropriétés.',
    landmarks: ['Base de loisirs de Vaires-Torcy', 'Centre commercial Bay 1', 'Gare RER A'],
    nearbyAreas: ['Noisiel', 'Noisy-le-Grand', 'Lognes', 'Bussy-Saint-Georges']
  },
  'champigny-sur-marne': {
    name: 'Champigny-sur-Marne',
    postalCode: '94500',
    department: 'Val-de-Marne',
    lat: 48.8175,
    lng: 2.5156,
    population: '78 000',
    specificities: 'Deuxième ville du Val-de-Marne, Champigny-sur-Marne présente une grande diversité d\'habitat, des immeubles HLM aux pavillons individuels, tous pouvant bénéficier d\'une amélioration énergétique.',
    landmarks: ['Bords de Marne', 'Parc du Tremblay', 'Marché du centre'],
    nearbyAreas: ['Villiers-sur-Marne', 'Chennevières-sur-Marne', 'Saint-Maur-des-Fossés', 'Le Perreux-sur-Marne']
  },
  'nogent-sur-marne': {
    name: 'Nogent-sur-Marne',
    postalCode: '94130',
    department: 'Val-de-Marne',
    lat: 48.8364,
    lng: 2.4831,
    population: '32 000',
    specificities: 'Ville bourgeoise aux nombreuses maisons de maître et villas du début du XXe siècle, Nogent-sur-Marne requiert une expertise en menuiserie haut de gamme respectant le patrimoine architectural.',
    landmarks: ['Pavillon Baltard', 'Bords de Marne', 'Île de Beauté'],
    nearbyAreas: ['Le Perreux-sur-Marne', 'Bry-sur-Marne', 'Joinville-le-Pont', 'Saint-Maur-des-Fossés']
  },
  'creteil': {
    name: 'Créteil',
    postalCode: '94000',
    department: 'Val-de-Marne',
    lat: 48.7904,
    lng: 2.4556,
    population: '93 000',
    specificities: 'Préfecture du Val-de-Marne, Créteil combine architecture des années 70 (quartier du Palais) et constructions récentes. La rénovation énergétique y représente un enjeu important pour les copropriétés.',
    landmarks: ['Lac de Créteil', 'Cathédrale Notre-Dame', 'Créteil Soleil'],
    nearbyAreas: ['Saint-Maur-des-Fossés', 'Maisons-Alfort', 'Bonneuil-sur-Marne', 'Choisy-le-Roi']
  }
};

// Générer FAQ unique pour chaque ville
const generateFAQ = (city) => {
  return [
    {
      question: `Quel est le prix d'une rénovation de fenêtres à ${city.name} ?`,
      answer: `Le prix d'une rénovation de fenêtres à ${city.name} (${city.postalCode}) varie selon le matériau choisi, les dimensions et le type de pose. En moyenne, comptez entre 350€ et 800€ pour une fenêtre PVC, 500€ à 1200€ pour l'aluminium. NGL SERVICES vous propose un devis gratuit et personnalisé à ${city.name}.`
    },
    {
      question: `NGL SERVICES intervient-il rapidement à ${city.name} ?`,
      answer: `Oui, NGL SERVICES assure des interventions rapides à ${city.name} et dans tout le ${city.department}. Pour les urgences (vitre cassée, serrure bloquée), nous intervenons en moins d'1 heure. Pour les projets de rénovation, nous proposons un rendez-vous sous 48h.`
    },
    {
      question: `Peut-on payer en plusieurs fois à ${city.name} ?`,
      answer: `Absolument ! NGL SERVICES propose une facilité de paiement en 3 à 24 fois pour tous vos projets de rénovation de fenêtres à ${city.name}. Cette solution vous permet de réaliser vos travaux sans impacter votre budget mensuel.`
    },
    {
      question: `Quels types de fenêtres installez-vous à ${city.name} ?`,
      answer: `Nous installons tous types de fenêtres à ${city.name} : PVC (meilleur rapport qualité-prix), aluminium (design contemporain), bois (charme authentique) et mixte bois-alu (premium). Nos techniciens vous conseilleront la solution adaptée à votre habitat.`
    },
    {
      question: `Intervenez-vous pour les urgences vitrerie à ${city.name} ?`,
      answer: `Oui, NGL SERVICES propose un service d'urgence vitrerie 24h/24 et 7j/7 à ${city.name}. Bris de glace, vitre cassée, mise en sécurité après effraction : appelez le 07 81 55 90 50 pour une intervention rapide.`
    },
    {
      question: `Le devis est-il gratuit à ${city.name} ?`,
      answer: `Oui, le devis et le déplacement sont 100% gratuits à ${city.name} et dans toutes les communes voisines (${city.nearbyAreas.slice(0, 3).join(', ')}). Notre technicien se déplace pour prendre les mesures et vous remettre un devis détaillé sans engagement.`
    }
  ];
};

const RenovationVillePage = ({ citySlug }) => {
  // Récupérer les données de la ville
  const city = citiesData[citySlug];
  
  const pageTitle = city ? `Rénovation fenêtres ${city.name} (${city.postalCode})` : 'Page non trouvée';
  const pageDescription = city ? `Rénovation de fenêtres à ${city.name}. Fenêtres PVC, aluminium, bois. Paiement en plusieurs fois. Devis gratuit ☎ 07 81 55 90 50. Vitrier ${city.department}.` : '';
  const canonical = city ? `https://www.menuisiervitrier.fr/renovation-fenetres-${citySlug}` : '';

  const faq = city ? generateFAQ(city) : [];

  // Force update SEO tags
  useEffect(() => {
    if (!city) return;
    
    document.title = pageTitle;
    
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical;
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.content = pageDescription;
    }
  }, [city, pageTitle, canonical, pageDescription]);

  if (!city) {
    return <div className="min-h-screen flex items-center justify-center">Ville non trouvée</div>;
  }

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
        <meta name="geo.region" content={`FR-${city.postalCode.substring(0, 2)}`} />
        <meta name="geo.placename" content={city.name} />
        <meta name="geo.position" content={`${city.lat};${city.lng}`} />
        
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "NGL SERVICES - Rénovation Fenêtres ${city.name}",
            "description": "${pageDescription}",
            "url": "${canonical}",
            "telephone": "+33781559050",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "${city.name}",
              "postalCode": "${city.postalCode}",
              "addressRegion": "${city.department}",
              "addressCountry": "FR"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": ${city.lat},
              "longitude": ${city.lng}
            },
            "priceRange": "€€",
            "paymentAccepted": ["Cash", "Credit Card", "Financing"]
          }
        `}</script>
        
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": ${JSON.stringify(faq.map(f => ({
              "@type": "Question",
              "name": f.question,
              "acceptedAnswer": { "@type": "Answer", "text": f.answer }
            })))}
          }
        `}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[hsl(215,25%,15%)] to-[hsl(207,78%,25%)] text-white">
        <div className="container mx-auto px-4">
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="text-white/70 hover:text-white">Accueil</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/services/fenetres" className="text-white/70 hover:text-white">Rénovation fenêtres</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-white">{city.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <Badge className="bg-[hsl(24,95%,53%)] text-white mb-4">
            <CreditCard className="w-4 h-4 mr-2" /> Paiement en plusieurs fois
          </Badge>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Rénovation de fenêtres à {city.name}<br />
            <span className="text-[hsl(24,95%,53%)]">({city.postalCode})</span>
          </h1>
          
          <p className="text-xl text-gray-200 mb-4">
            <strong>Fenêtres PVC • Aluminium • Bois</strong> – Installation sur mesure en {city.department}
          </p>
          
          <p className="text-lg text-gray-300 mb-8 max-w-3xl">
            NGL SERVICES, votre spécialiste de la <strong>rénovation de fenêtres à {city.name}</strong>. 
            Profitez de notre solution de <strong>paiement en plusieurs fois</strong> pour remplacer 
            vos fenêtres et améliorer l'isolation de votre logement.
          </p>

          <div className="flex flex-wrap gap-4 mb-8">
            <Link to="/contact">
              <Button size="lg" className="emergency-btn rounded-full text-lg px-8">
                <ArrowRight className="w-5 h-5 mr-2" />
                Devis gratuit – Paiement facilité
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phoneEmergency}`}>
              <Button size="lg" className="bg-white text-[hsl(207,78%,35%)] hover:bg-gray-100 rounded-full text-lg px-8">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phoneEmergency}
              </Button>
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-6 text-white/90">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[hsl(24,95%,53%)]" />
              <span>Devis & déplacement gratuits</span>
            </div>
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[hsl(24,95%,53%)]" />
              <span>Paiement 3 à 24 mois</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[hsl(24,95%,53%)]" />
              <span>Garantie 10 ans</span>
            </div>
          </div>
        </div>
      </section>

      {/* Section spécifique à la ville */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-6">
                Pourquoi rénover vos fenêtres à {city.name} ?
              </h2>
              
              <p className="text-muted-foreground mb-4">
                {city.specificities}
              </p>
              
              <p className="text-muted-foreground mb-4">
                Avec une population de <strong>{city.population} habitants</strong>, {city.name} présente 
                un parc immobilier diversifié où la <strong>rénovation énergétique</strong> représente 
                un enjeu majeur. Le remplacement de vos anciennes fenêtres peut vous faire économiser 
                jusqu'à <strong>30% sur vos factures de chauffage</strong>.
              </p>

              <p className="text-muted-foreground mb-6">
                NGL SERVICES intervient dans tous les quartiers de {city.name}, notamment près de {' '}
                {city.landmarks.join(', ')}. Nous couvrons également les communes voisines : {' '}
                {city.nearbyAreas.join(', ')}.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Zap, text: "Économies d'énergie -30%" },
                  { icon: Shield, text: "Sécurité renforcée" },
                  { icon: ThumbsUp, text: "Confort thermique" },
                  { icon: Award, text: "Valorisation +10%" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-3 bg-[hsl(210,20%,98%)] rounded-lg">
                    <item.icon className="w-5 h-5 text-[hsl(24,95%,53%)]" />
                    <span className="text-sm font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-[hsl(207,78%,95%)] rounded-2xl p-8">
              <h3 className="text-xl font-bold text-[hsl(215,25%,15%)] mb-6">
                Nos services à {city.name}
              </h3>
              <div className="space-y-4">
                {[
                  { icon: Home, title: "Rénovation fenêtres", desc: "PVC, aluminium, bois – Sur mesure" },
                  { icon: Wrench, title: "Vitrerie & miroiterie", desc: "Double vitrage, vitrage sécurité" },
                  { icon: Key, title: "Serrurerie", desc: "Ouverture de porte dès 90€" },
                  { icon: Clock, title: "Urgences 24h/24", desc: "Intervention rapide 7j/7" }
                ].map((service, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-white rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-[hsl(207,78%,42%)] flex items-center justify-center flex-shrink-0">
                      <service.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[hsl(215,25%,15%)]">{service.title}</h4>
                      <p className="text-sm text-muted-foreground">{service.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types de fenêtres */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] text-center mb-4">
            Types de fenêtres disponibles à {city.name}
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            NGL SERVICES vous propose une gamme complète de menuiseries adaptées à tous les styles 
            d'habitat de {city.name}.
          </p>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { title: "Fenêtres PVC", price: "À partir de 350€", features: ["Excellent rapport qualité-prix", "Isolation optimale", "Entretien minimal"] },
              { title: "Fenêtres Aluminium", price: "À partir de 500€", features: ["Design contemporain", "Profilés fins", "Grande durabilité"] },
              { title: "Fenêtres Bois", price: "À partir de 450€", features: ["Charme authentique", "Isolation naturelle", "Écologique"] },
              { title: "Baies vitrées", price: "À partir de 800€", features: ["Luminosité maximale", "Ouverture coulissante", "Sur mesure"] }
            ].map((type, idx) => (
              <Card key={idx} className="border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-bold text-[hsl(215,25%,15%)] mb-2">{type.title}</h3>
                  <p className="text-[hsl(24,95%,53%)] font-bold mb-4">{type.price}</p>
                  <ul className="space-y-2 text-sm text-left">
                    {type.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] text-center mb-4">
            Questions fréquentes – Rénovation fenêtres {city.name}
          </h2>
          <p className="text-muted-foreground text-center mb-12">
            Retrouvez les réponses aux questions les plus courantes
          </p>
          
          <div className="max-w-3xl mx-auto">
            <FAQSection faqs={faq} />
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-[hsl(215,25%,15%)] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Prêt à rénover vos fenêtres à {city.name} ?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Contactez NGL SERVICES pour un devis gratuit. Paiement en plusieurs fois disponible.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="emergency-btn rounded-full text-lg px-8">
                <ArrowRight className="w-5 h-5 mr-2" />
                Devis gratuit – Paiement facilité
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phoneEmergency}`}>
              <Button size="lg" className="bg-white text-[hsl(215,25%,15%)] hover:bg-gray-100 rounded-full text-lg px-8">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phoneEmergency}
              </Button>
            </a>
          </div>
          
          <p className="mt-8 text-gray-400 text-sm">
            NGL SERVICES – Vitrier et menuisier à {city.name} ({city.postalCode}) – {city.department}
          </p>
        </div>
      </section>
    </div>
  );
};

export default RenovationVillePage;
export { citiesData };
